import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '../lib/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'teacher';
  status: string;
  phone?: string;
  instituteId?: number | null;
  instituteName?: string;
  instituteSlug?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRehydrated: boolean;
  lastActiveAt: number;
  originalToken: string | null;
  originalUser: User | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setUser: (user: User) => void;
  updateLastActive: () => void;
  setHasRehydrated: () => void;
  assumeInstitute: (instituteId: number, instituteName: string) => Promise<void>;
  exitInstituteView: () => void;
  isImpersonating: () => boolean;
}

const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD = 60 * 60 * 1000;

function decodeJWT(token: string): { userId: number; email: string; role: string; instituteId?: number; exp: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded) return true;
  return decoded.exp * 1000 < Date.now();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      hasRehydrated: false,
      lastActiveAt: Date.now(),
      originalToken: null,
      originalUser: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();
          if (!data.success) {
            throw new Error(data.error?.message || 'Login failed');
          }

          set({
            user: data.data.user,
            token: data.data.token,
            isAuthenticated: true,
            isLoading: false,
            lastActiveAt: Date.now(),
            originalToken: null,
            originalUser: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          lastActiveAt: 0,
          originalToken: null,
          originalUser: null,
        });
      },

      checkAuth: async () => {
        const { token, lastActiveAt } = get();

        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }

        if (Date.now() - lastActiveAt > SESSION_TIMEOUT) {
          get().logout();
          return;
        }

        if (isTokenExpired(token)) {
          get().logout();
          return;
        }

        set({ isLoading: true });
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.json();
          if (data.success) {
            set({
              user: data.data.user || data.data,
              isAuthenticated: true,
              isLoading: false,
              lastActiveAt: Date.now(),
            });
          } else {
            get().logout();
          }
        } catch {
          get().logout();
        } finally {
          set({ isLoading: false });
        }
      },

      refreshSession: async () => {
        const { token } = get();
        if (!token) return false;

        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await response.json();
          if (data.success) {
            set({
              user: data.data.user || data.data,
              isAuthenticated: true,
              lastActiveAt: Date.now(),
            });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      updateLastActive: () => {
        set({ lastActiveAt: Date.now() });
      },

      setHasRehydrated: () => {
        set({ hasRehydrated: true });
      },

      assumeInstitute: async (instituteId: number, instituteName: string) => {
        const { token: currentToken, user: currentUser } = get();
        if (!currentToken || !currentUser || currentUser.role !== 'super_admin') {
          throw new Error('Only super admin can assume institutes');
        }

        const response = await fetch(`${API_URL}/api/auth/assume/${instituteId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}` },
        });

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error?.message || 'Failed to assume institute');
        }

        set({
          originalToken: currentToken,
          originalUser: currentUser,
          token: data.data.token,
          user: {
            ...currentUser,
            role: 'admin',
            instituteId: instituteId,
            instituteName: instituteName,
          },
          lastActiveAt: Date.now(),
        });

        window.location.href = '/dashboard';
      },

      exitInstituteView: () => {
        const { originalToken, originalUser } = get();
        if (originalToken && originalUser) {
          set({
            token: originalToken,
            user: originalUser,
            originalToken: null,
            originalUser: null,
            lastActiveAt: Date.now(),
          });
          window.location.href = '/admin/institutes';
        }
      },

      isImpersonating: () => {
        const { originalToken } = get();
        return !!originalToken;
      },
    }),
    {
      name: 'paathshaala-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastActiveAt: state.lastActiveAt,
        originalToken: state.originalToken,
        originalUser: state.originalUser,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasRehydrated();
        }
      },
    }
  )
);

if (typeof window !== 'undefined') {
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

  activityEvents.forEach(event => {
    window.addEventListener(event, () => {
      const { token, isAuthenticated } = useAuthStore.getState();
      if (token && isAuthenticated) {
        useAuthStore.getState().updateLastActive();
      }
    }, { passive: true });
  });

  setInterval(() => {
    const { token, isAuthenticated, lastActiveAt } = useAuthStore.getState();
    if (token && isAuthenticated) {
      if (Date.now() - lastActiveAt > 23 * 60 * 60 * 1000) {
        useAuthStore.getState().refreshSession();
      }
    }
  }, 15 * 60 * 1000);
}
